import { Close } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Box, Card, Chip, Divider, List, ListItem, ListItemText, styled, ThemeProvider } from "@mui/material";
import toast from "react-hot-toast";

import { baseApi } from "../../common/constants";
import { Paragraph } from "./components/Typography";
import { createCustomTheme } from "./theme";
import { Context } from "../../common/context";
import { Axios, currencyFormat, dateFormat, escInput } from "../../common/utils";
import { AccountModal } from "./accountPieces";
import { GradientTop } from "./components/pieces";
import type { Size } from "../../types";

interface OrderDetails {
    logo?: string;
    urla?: string;
    urlb?: string;
    orderId?: string;
    storeName?: string;
    exclusions?: string;
    cashback?: number | string;
    date?: string | number | Date | null;
    sale?: number | string;
    payout?: string;
    status?: string;
}

const TextLine = styled(Box)(() => ({
    textAlign: `center`,
    fontWeight: 700,
    fontSize: `16px`,
    padding: `50px 0 0`,
    color: ` #9e9e9e`,
}));

const chipMap: Record<string, "info" | "error" | "success"> = {
    Pending: `info`,
    Ineligible: `error`,
    Earned: `success`,
};

interface DetailsProps {
    showDetails: string;
    closeDetails: () => void;
    size: Size;
}

const Details = ({ showDetails, closeDetails, size }: DetailsProps) => {
    const [details, setDetails] = useState<OrderDetails | Record<string, never> | undefined>();
    const [opacity, setOpacity] = useState(0);
    const [height, setHeight] = useState<number | string>(50);
    useEffect(() => {
        Axios.get(`${baseApi}details?orderid=${encodeURIComponent(showDetails)}`)
            .then(r => {
                setDetails(r.data);
                setOpacity(1);
                setTimeout(() => setHeight(`unset`), 300);
            })
            .catch(() => {
                toast.error(`Sorry something went wrong`);
                setDetails({});
            });
    }, []);

    const MobilePara = styled(Paragraph)(() => ({
        fontSize: 16,
    }));

    return (
        <Context.Consumer>
            {value => (
                <ThemeProvider theme={createCustomTheme({ context: value.context })}>
                    <AccountModal
                        onKeyDown={e => escInput(e, closeDetails)}
                        size={size}
                        sx={{ opacity, transition: `opacity .6s ease` }}
                        open={!!showDetails}
                        onClose={closeDetails}
                    >
                        <Card className="modalContent" sx={{ mt: 3, outline: `none` }}>
                            {size !== `small` && <GradientTop />}
                            <Box
                                sx={{
                                    p: 4,
                                    pt: 1.6,
                                    margin: `20px auto 0`,
                                    maxWidth: 500,
                                    height: `100%`,
                                }}
                            >
                                <Close
                                    sx={{
                                        position: `absolute`,
                                        top: 24,
                                        right: 10,
                                        zIndex: 30,
                                        cursor: `pointer`,
                                        opacity: 0.55,
                                        height: 25,
                                        width: 25,
                                        zindex: 30,
                                    }}
                                    onClick={closeDetails}
                                />
                                {!!details && Object.keys(details).length > 0 && (
                                    <Box sx={{ textAlign: `center`, fontSize: `24px`, pt: 2.5, px: 2 }}>
                                        <Box
                                            component="img"
                                            src={
                                                details.logo ||
                                                `https://s3.amazonaws.com/retailerimages/logos/${details.urla}/${details.urlb}`
                                            }
                                            sx={{ maxWidth: `200px`, maxHeight: `50px`, height }}
                                        />
                                        <MobilePara sx={{ fontSize: 14, mt: 0 }}>Order: {details.orderId}</MobilePara>
                                        <Divider sx={{ marginBlock: 4.5 }} />
                                        {!details.cashback && (
                                            <Box>
                                                <List sx={{ width: `100%` }}>
                                                    <ListItem
                                                        {...({ disableGutter: true } as { disableGutter?: boolean })}
                                                    >
                                                        <ListItemText
                                                            primary={`Excludes purchases made through the ${
                                                                details?.storeName || `merchant's`
                                                            } app`}
                                                        />
                                                    </ListItem>
                                                    <ListItem
                                                        {...({ disableGutter: true } as { disableGutter?: boolean })}
                                                    >
                                                        <ListItemText primary="Excludes purchase of gift cards" />
                                                    </ListItem>
                                                    {!!details.exclusions && (
                                                        <ListItem
                                                            {...({ disableGutter: true } as {
                                                                disableGutter?: boolean;
                                                            })}
                                                        >
                                                            <ListItemText
                                                                primary={`Other Exclusions: ${details.exclusions}`}
                                                            />
                                                        </ListItem>
                                                    )}
                                                </List>
                                            </Box>
                                        )}
                                        {!!details.cashback && (
                                            <Box sx={{ textAlign: `left` }}>
                                                <MobilePara px={5} sx={{ fontWeight: 600 }}>
                                                    Order Date:
                                                    <Box sx={{ float: `right`, fontWeight: 500 }}>
                                                        {dateFormat(details.date)}
                                                    </Box>
                                                </MobilePara>
                                                <MobilePara px={5} sx={{ fontWeight: 600 }}>
                                                    Order Amount:
                                                    <Box sx={{ float: `right`, fontWeight: 500 }}>
                                                        ${currencyFormat(details.sale as number | string)}
                                                    </Box>
                                                </MobilePara>
                                                <MobilePara px={5} sx={{ fontWeight: 600 }}>
                                                    Cash Back:
                                                    <Box sx={{ float: `right`, fontWeight: 500 }}>
                                                        ${currencyFormat(details.cashback as number | string)}
                                                    </Box>
                                                </MobilePara>
                                                <MobilePara px={5} sx={{ fontWeight: 600 }}>
                                                    Payout Date:
                                                    <Box sx={{ float: `right`, fontWeight: 500 }}>{details.payout}</Box>
                                                </MobilePara>
                                                <MobilePara px={5} sx={{ fontWeight: 600 }}>
                                                    Status:
                                                    <Box sx={{ float: `right`, fontWeight: 500 }}>
                                                        <Box
                                                            sx={{
                                                                display: `flex`,
                                                                alignItems: `center`,
                                                                justifyContent: `center`,
                                                                float: `right`,
                                                            }}
                                                        >
                                                            <Chip
                                                                sx={{ padding: `2px 8px 0` }}
                                                                size="small"
                                                                label={details.status}
                                                                color={chipMap[details.status as string]}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </MobilePara>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                                {!!details && !Object.keys(details).length && <TextLine>No results found.</TextLine>}
                            </Box>
                        </Card>
                    </AccountModal>
                </ThemeProvider>
            )}
        </Context.Consumer>
    );
};

export default Details;
